import React from 'react';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Button,
  Icon,
  useToast,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../services/api';

const VerifyEmailPrompt: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as any)?.email || 'your email address';
  const emailSent = (location.state as any)?.emailSent !== false;
  const toast = useToast();
  const [isResending, setIsResending] = React.useState(false);

  const resend = async () => {
    if (email === 'your email address') return;
    setIsResending(true);
    try {
      await api.resendVerification(email);
      toast({ title: 'Verification email sent', status: 'success', duration: 5000 });
    } catch (error) {
      toast({ title: 'Email could not be sent', description: error instanceof Error ? error.message : undefined, status: 'error', duration: 5000 });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Box minH="80vh" bg="white" display="flex" alignItems="center" py={12}>
      <Container maxW="500px">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Box
            bg="white"
            border="1px solid"
            borderColor="neutral.200"
            borderRadius="16px"
            p={8}
            boxShadow="0 4px 20px rgba(0, 0, 0, 0.06)"
            textAlign="center"
          >
            <VStack spacing={6}>
              {/* Mail Icon Circle */}
              <Box
                w="80px"
                h="80px"
                borderRadius="full"
                bg="primary.50"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon
                  viewBox="0 0 24 24"
                  boxSize="40px"
                  color="primary.500"
                >
                  <path
                    fill="currentColor"
                    d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                  />
                </Icon>
              </Box>

              <VStack spacing={3}>
                <Heading size="lg" color="neutral.900" fontWeight="700">
                  Verify your email to login
                </Heading>
                <Text color="neutral.600" fontSize="15px" lineHeight="1.6">
                  {emailSent ? "We've sent a verification link to " : 'Your account was created, but email delivery failed for '}
                  <Text as="span" fontWeight="700" color="neutral.800">
                    {email}
                  </Text>
                  . {emailSent ? 'Please check your inbox and click the link to activate your account.' : 'Use the button below to retry.'}
                </Text>
              </VStack>

              <Box bg="neutral.50" p={4} borderRadius="8px" w="full" border="1px border" borderColor="neutral.200">
                <Text fontSize="13px" color="neutral.500">
                  Didn't receive an email? Check your spam folder or try logging in to resend the verification link.
                </Text>
              </Box>

              <Button
                colorScheme="brand"
                variant="outline"
                w="full"
                isLoading={isResending}
                isDisabled={email === 'your email address'}
                onClick={resend}
              >
                Resend verification email
              </Button>

              <Button
                colorScheme="brand"
                w="full"
                h="44px"
                fontSize="15px"
                borderRadius="8px"
                onClick={() => navigate('/login')}
              >
                Go to Login Page
              </Button>
            </VStack>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default VerifyEmailPrompt;
